// Health / classification / series enrichment for the Plant Viewer's
// selected live tag.
//
// Identity stays graph — the Plant Viewer resolves tags from `:Tag` nodes.
// This component joins the acquisition row through `tags.node_id = :Tag.id`,
// then uses its canonical path for readings, stats, and health.
//
// AVEVA tags have no registry row and no tag_readings — they render an
// explicit "no acquisition data" note instead of erroring.

import * as React from 'react'
import { Loader2 } from 'lucide-react'
import {
  tagClient,
  useTagClassificationsQuery,
  useTagSummaryQuery,
  type TagRecord
} from '@axilon-apps/domains-ontology-ui-state'

import { Badge } from '../../components/ui/badge'
import { evaluateTagHealth } from '../TagsPage/tag-health'
import TagSeriesChart from '../TagsPage/TagSeriesChart'

const SERIES_WINDOW_SECS = 3600

/** Escape a value for an exact-match AIP-160 clause. */
function exactFilter(field: string, value: string): string {
  return `${field}="${value.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"`
}

interface RegistryResolution {
  tag: TagRecord | null
  isLoading: boolean
  error: string | undefined
}

/** Resolve the registry row linked to one graph Tag ID. */
function useRegistryTagForNodeId(nodeId: string): RegistryResolution {
  const [state, setState] = React.useState<RegistryResolution>({
    tag: null,
    isLoading: Boolean(nodeId),
    error: undefined
  })

  React.useEffect(() => {
    if (!nodeId) {
      setState({ tag: null, isLoading: false, error: undefined })
      return
    }
    let cancelled = false
    setState({ tag: null, isLoading: true, error: undefined })
    const client = tagClient()
    const lookup = async (): Promise<TagRecord | null> => {
      const direct = await client.listTags({ filter: exactFilter('node_id', nodeId) })
      return direct.tags.find((tag) => tag.nodeId === nodeId) ?? null
    }
    lookup()
      .then((tag) => {
        if (!cancelled) setState({ tag, isLoading: false, error: undefined })
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setState({
            tag: null,
            isLoading: false,
            error: err instanceof Error ? err.message : String(err)
          })
        }
      })
    return () => {
      cancelled = true
    }
  }, [nodeId])

  return state
}

export interface PlantTagHealthProps {
  /** Graph `:Tag.id`, used for the authoritative registry join. */
  nodeId: string
  /** Persisted graph path; absence means no canonical graph identity. */
  tagPath: string | undefined
}

export function PlantTagHealth({
  nodeId,
  tagPath
}: PlantTagHealthProps): React.ReactElement | null {
  const registry = useRegistryTagForNodeId(tagPath ? nodeId : '')

  if (!tagPath) return null
  if (registry.isLoading) {
    return (
      <div className="flex items-center gap-1.5 text-11 text-ink-3">
        <Loader2 className="size-3 animate-spin" aria-hidden />
        Checking history…
      </div>
    )
  }
  if (registry.error) {
    return <div className="text-11 text-st-warn">History lookup failed.</div>
  }
  if (!registry.tag) {
    // Graph-only tag (e.g. AVEVA): identity exists, acquisition doesn't.
    return (
      <div className="text-11 text-ink-3" data-testid="plant-tag-no-acquisition">
        No history data — this tag isn’t collected into the historian.
      </div>
    )
  }
  return <ResolvedTagHealth registryTag={registry.tag} graphNodeId={nodeId} />
}

const HEALTH_BADGE: Record<string, { label: string; className: string }> = {
  bad: { label: 'Problem', className: 'bg-st-down-bg text-st-down' },
  warn: { label: 'Warning', className: 'bg-st-warn-bg text-st-warn' },
  ok: { label: 'Healthy', className: 'bg-st-run-bg text-st-run' }
}

function ResolvedTagHealth({
  registryTag,
  graphNodeId
}: {
  registryTag: TagRecord
  graphNodeId: string
}): React.ReactElement {
  const summaryQuery = useTagSummaryQuery({
    sourceUrl: registryTag.sourceUrl,
    sourceType: registryTag.sourceType,
    tagPath: registryTag.tagPath,
    tagId: registryTag.id
  })
  // LLM classification lives on the graph :Tag node and is keyed by its URN.
  const classificationsQuery = useTagClassificationsQuery(
    React.useMemo(() => [graphNodeId], [graphNodeId])
  )

  const verdict = React.useMemo(
    () =>
      evaluateTagHealth(
        classificationsQuery.data?.get(graphNodeId),
        summaryQuery.data ?? undefined
      ),
    [classificationsQuery.data, graphNodeId, summaryQuery.data]
  )

  const badge = HEALTH_BADGE[verdict.status]
  const visibleFindings = verdict.findings.filter((f) => f.code !== 'no_recent_readings')

  return (
    <div className="flex flex-col gap-2" data-testid="plant-tag-health">
      <div className="flex items-center gap-1.5">
        {summaryQuery.isLoading ? (
          <span className="inline-flex items-center gap-1.5 text-11 text-ink-3">
            <Loader2 className="size-3 animate-spin" aria-hidden />
            assessing…
          </span>
        ) : badge ? (
          <span
            className={`inline-flex items-center rounded-full px-2 py-0.5 text-11 font-semibold ${badge.className}`}
            data-testid="plant-tag-health-chip"
          >
            {badge.label}
          </span>
        ) : (
          <span className="text-11 text-ink-3">No health signal yet.</span>
        )}
        {registryTag.scrapeMode === 'subscribe' ? (
          <Badge variant="outline" className="text-10" title="Acquired over OPC UA subscribe">
            OPC subscribe
          </Badge>
        ) : null}
      </div>
      {visibleFindings.length > 0 ? (
        <ul className="m-0 flex list-none flex-col gap-0.5 p-0 text-11 text-ink-2">
          {visibleFindings.slice(0, 3).map((f) => (
            <li key={f.code}>{f.message}</li>
          ))}
        </ul>
      ) : null}
      <div className="h-28 min-h-0">
        <TagSeriesChart tag={registryTag} windowSecs={SERIES_WINDOW_SECS} className="h-full" />
      </div>
    </div>
  )
}

export default PlantTagHealth
